import React from 'react';
import { Link } from 'react-router-dom';
import { SectionCard } from '../../layout/memberPrimitives';
import type { BiomarkerDelta, BiomarkerMovement } from '../../../utils/dashboard/biomarkerDeltas';
import type { WearableDelta } from '../../../utils/dashboard/wearableDeltas';
import type { ReviewAnchor } from '../../../utils/dashboard/reviewAnchor';
import type { SourceStatus } from '../../../utils/dashboard/types';

const MOVEMENT_COLOR: Record<BiomarkerMovement, string> = {
  toward_range: 'var(--melio-olive-deep)',
  away_from_range: 'var(--melio-terracotta-deep)',
  stable: 'var(--melio-text-muted)',
};

function formatNumber(value: number): string {
  const rounded = Math.abs(value) >= 100 ? Math.round(value) : Math.round(value * 100) / 100;
  return String(rounded);
}

interface DeltaCardProps {
  title: string;
  from: string;
  to: string;
  arrow: string;
  change: string;
  movement: BiomarkerMovement;
  footnote: string;
}

function DeltaCard({ title, from, to, arrow, change, movement, footnote }: DeltaCardProps) {
  const color = MOVEMENT_COLOR[movement];
  return (
    <li
      className="rounded-2xl border p-4"
      style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--melio-cream-warm)' }}
    >
      <div className="flex items-baseline gap-2 flex-wrap mb-1.5">
        <span className="text-sm font-medium" style={{ color: 'var(--melio-ink)' }}>
          {title}
        </span>
        <span className="text-sm font-semibold" style={{ color }} aria-hidden="true">
          {arrow} {change}
        </span>
      </div>
      <p className="text-sm tabular-nums" style={{ color: 'var(--melio-ink)' }}>
        {from} → {to}
      </p>
      <p className="text-[11px] mt-1" style={{ color: 'var(--melio-text-muted)' }}>
        {footnote}
      </p>
    </li>
  );
}

/**
 * "What's changed" — movement in the member's own numbers.
 *
 * Deliberately says how much something moved and in which direction, and stops
 * there. Whether a single marker moving is an improvement is a clinical
 * judgement the catalogue copy is still awaiting sign-off to make.
 *
 * Hidden entirely when there is nothing to compare AND there is nothing on file
 * — an empty "what's changed" card tells a new member less than no card at all.
 *
 * The exception is the member who has exactly one panel. Deltas need two
 * readings of a marker, so their block collapsed too — which meant the member
 * whose first results had just arrived saw the emptiest Home in the product, on
 * the day they had most reason to look. They are not missing anything; there is
 * simply nothing yet to move against. Say that instead of vanishing.
 */
export default function WhatsChangedSection({
  anchor,
  biomarkers,
  wearables,
  status,
  baseline = null,
}: {
  anchor: ReviewAnchor;
  biomarkers: BiomarkerDelta[];
  wearables: WearableDelta[];
  status: SourceStatus;
  /** Set only when the member has exactly one draw on file. */
  baseline?: { count: number; observedAt: Date } | null;
}) {
  if (status === 'failed') {
    return (
      <SectionCard eyebrow="What's changed" title="We couldn't load your results just now">
        <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
          Refresh in a moment, or open{' '}
          <Link to="/intelligence" className="underline">
            Intelligence
          </Link>{' '}
          to see your full record.
        </p>
      </SectionCard>
    );
  }

  if (biomarkers.length === 0 && wearables.length === 0) {
    if (!baseline) return null;
    return (
      <SectionCard eyebrow="What's changed" title="This is your baseline">
        <p className="text-sm leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
          Your first {baseline.count === 1 ? 'result' : `${baseline.count} markers`} came back on{' '}
          {baseline.observedAt.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          . There is nothing to compare them against yet — your next panel is what turns these numbers into a
          direction of travel.
        </p>
        <p className="mt-3">
          <Link to="/results" className="text-sm underline" style={{ color: 'var(--melio-olive-deep)' }}>
            See your results →
          </Link>
        </p>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      eyebrow={`What's changed ${anchor.source === 'none' ? '' : `· ${anchor.label}`}`.trim()}
      title="Movement in your numbers"
      rightSlot={
        <Link to="/intelligence" className="text-xs underline" style={{ color: 'var(--melio-text-muted)' }}>
          See all trends →
        </Link>
      }
    >
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 list-none p-0 m-0">
        {biomarkers.map((delta) => (
          <DeltaCard
            key={delta.key}
            title={delta.label}
            from={`${formatNumber(delta.from)} ${delta.unit}`}
            to={`${formatNumber(delta.to)} ${delta.unit}`}
            arrow={delta.arrow}
            change={delta.pctChange === null ? formatNumber(Math.abs(delta.absChange)) : `${Math.abs(Math.round(delta.pctChange))}%`}
            movement={delta.movement}
            footnote={`${delta.fromLabel} → ${delta.toLabel}`}
          />
        ))}
        {wearables.map((delta) => (
          <DeltaCard
            key={delta.metricId}
            title={delta.label}
            from={delta.fromLabel}
            to={delta.toLabel}
            arrow={delta.arrow}
            change={delta.changeLabel}
            movement={delta.movement}
            footnote={`${delta.currentDays}-day average vs the ${delta.priorDays} days before`}
          />
        ))}
      </ul>
      <p className="text-[11px] mt-3" style={{ color: 'var(--melio-text-muted)' }}>
        These are your measurements, not an interpretation of them. Your clinician reads them in context at
        your next review.
      </p>
    </SectionCard>
  );
}
